// Web Push subscription management (spec §9). The server holds the VAPID
// public key; the subscription is registered per device.
import { api, db } from '@/runtime'
import { META } from '@/db/schema'

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

const urlBase64ToUint8Array = (b64: string) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4)
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

const sameKey = (have: ArrayBuffer | null, want: Uint8Array) => {
  if (!have) return false
  const a = new Uint8Array(have)
  return a.length === want.length && a.every((b, i) => b === want[i])
}

export const subscribePush = async () => {
  if (!pushSupported()) throw new Error('Push is not available in this browser.')
  const cfg = await api.config()
  if (!cfg.push_enabled) throw new Error('The server has no push keys configured.')
  const perm = await Notification.requestPermission()
  if (perm !== 'granted') throw new Error('Notifications were not allowed.')
  const reg = await navigator.serviceWorker.ready
  const key = urlBase64ToUint8Array(cfg.vapid_public)
  // A subscription made under a previous server key is refused by the push service
  // (Apple answers 403 BadJwtToken), so only reuse one whose key still matches.
  let sub = await reg.pushManager.getSubscription()
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe()
    sub = null
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  const id = await db.getMeta<string>(META.deviceId)
  const name = await db.getMeta<string>(META.deviceName)
  if (!id) throw new Error('Device not registered yet.')
  await api.registerDevice({ id, ...(name ? { name } : {}), push_sub: sub.toJSON() })
  await db.setMeta(META.pushSubscribed, true)
}

export const unsubscribePush = async () => {
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (sub) await sub.unsubscribe()
  const id = await db.getMeta<string>(META.deviceId)
  if (id) await api.registerDevice({ id, clear_push: true })
  await db.setMeta(META.pushSubscribed, false)
}

/**
 * Tell the server this device exists, on every start. Device rows can go
 * (a Forget in Settings → Security, a wiped database), and a device that is
 * still in use must come back by itself, with the push subscription the
 * browser is still holding, or notifications stop without anyone noticing.
 */
export const ensureRegistered = async () => {
  const id = await db.getMeta<string>(META.deviceId)
  if (!id) return
  const name = await db.getMeta<string>(META.deviceName)
  let push_sub: unknown
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const reg = await navigator.serviceWorker?.ready
      const sub = await reg?.pushManager.getSubscription()
      if (sub) push_sub = sub.toJSON()
    }
  } catch {
    /* no subscription to carry: the row is still worth having */
  }
  await api
    .registerDevice({ id, ...(name ? { name } : {}), ...(push_sub ? { push_sub } : {}) })
    .catch(() => {})
}
